import React, { useState, useEffect } from 'react';
import { ref, get, set, remove, push } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AdminSlider = () => {
  const [sliderImages, setSliderImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [currentImageId, setCurrentImageId] = useState(null);
  const [altTexts, setAltTexts] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);
  const [hoverIndex, setHoverIndex] = useState(null);

  // Fetch images from db
  useEffect(() => {
    const fetchSliderImages = async () => {
      try {
        setLoading(true);
        const sliderRef = ref(db, 'Watch/admin/slider/images');
        const snapshot = await get(sliderRef);
        
        if (snapshot.exists()) {
          const images = Object.entries(snapshot.val()).map(([id, image]) => ({
            id,
            ...image,
            order: image.order || 0 // Add order property if not exists
          }));
          // Sort images by order
          images.sort((a, b) => a.order - b.order);
          setSliderImages(images);
        } else {
          setSliderImages([]);
        }
      } catch (error) {
        toast.error('Failed to load slider images');
        console.error("db error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSliderImages();
  }, []);

  // Handle multiple image selection
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const validImages = files.filter(file => file.type.startsWith('image/'));
    
    if (validImages.length === 0) {
      toast.error('Please select valid image files (JPEG, PNG, etc.)');
      return;
    }
    
    if (files.length !== validImages.length) {
      toast.warning(`Some files were not images and were ignored. ${validImages.length} valid images selected.`);
    }
    
    setNewImages(validImages);
    setPreviewImages(validImages.map(file => URL.createObjectURL(file)));
    setAltTexts(validImages.map(() => ''));
  };

  // Upload multiple images to Firebase
  const uploadImages = async () => {
    if (newImages.length === 0) {
      toast.warning('Please select images first');
      return;
    }

    try {
      setLoading(true);
      const uploadPromises = newImages.map(async (image, index) => {
        // Upload to Firebase Storage
        const storagePath = `slider-images/${Date.now()}_${index}_${image.name}`;
        const imageRef = storageRef(storage, storagePath);
        await uploadBytes(imageRef, image);
        const downloadURL = await getDownloadURL(imageRef);

        // Save metadata to Realtime db
        const newImageRef = push(ref(db, 'Watch/admin/slider/images'));
        await set(newImageRef, {
          url: downloadURL,
          storagePath: storagePath,
          filename: image.name,
          uploadedAt: Date.now(),
          altText: altTexts[index] || `Slider Image ${sliderImages.length + index + 1}`,
          order: sliderImages.length + index // Set initial order
        });
      });

      await Promise.all(uploadPromises);
      toast.success(`${newImages.length} images uploaded successfully!`);
      refreshImages();
      resetForm();
    } catch (error) {
      toast.error('Failed to upload images');
      console.error("Upload error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Update existing image
  const updateImage = async () => {
    if (!newImages[0] && !altTexts[0]) {
      toast.warning('No changes detected');
      return;
    }

    try {
      setLoading(true);
      const imageRef = ref(db, `Watch/admin/slider/images/${currentImageId}`);
      const imageData = sliderImages.find(img => img.id === currentImageId);

      let downloadURL = imageData.url;
      let storagePath = imageData.storagePath;

      // If new image is selected, upload it
      if (newImages[0]) {
        // Delete old image from storage
        if (imageData.storagePath) {
          const oldImageRef = storageRef(storage, imageData.storagePath);
          await deleteObject(oldImageRef).catch(console.error);
        }

        // Upload new image
        storagePath = `slider-images/${Date.now()}_${newImages[0].name}`;
        const newImageRef = storageRef(storage, storagePath);
        await uploadBytes(newImageRef, newImages[0]);
        downloadURL = await getDownloadURL(newImageRef);
      }

      // Update db record
      await set(imageRef, {
        ...imageData,
        url: downloadURL,
        storagePath: storagePath,
        filename: newImages[0] ? newImages[0].name : imageData.filename,
        altText: altTexts[0] || imageData.altText
      });

      toast.success('Image updated successfully!');
      refreshImages();
      resetForm();
    } catch (error) {
      toast.error('Failed to update image');
      console.error("Update error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Delete image from both Storage and db
  const deleteImage = async (imageId, imageData) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      setLoading(true);
      
      // Delete from Storage
      if (imageData.storagePath) {
        const imageRef = storageRef(storage, imageData.storagePath);
        await deleteObject(imageRef);
      }

      // Delete from db
      const imageDbRef = ref(db, `Watch/admin/slider/images/${imageId}`);
      await remove(imageDbRef);

      toast.success('Image deleted successfully!');
      refreshImages();
    } catch (error) {
      toast.error('Failed to delete image');
      console.error("Delete error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Reorder images in the db
  const reorderImages = async (draggedIndex, targetIndex) => {
    if (draggedIndex === targetIndex) return;
    
    try {
      setLoading(true);
      
      // Create a new array with updated order
      const updatedImages = [...sliderImages];
      const [movedImage] = updatedImages.splice(draggedIndex, 1);
      updatedImages.splice(targetIndex, 0, movedImage);
      
      // Update order property for all images
      const updatePromises = updatedImages.map((image, index) => {
        const imageRef = ref(db, `Watch/admin/slider/images/${image.id}`);
        return set(imageRef, {
          ...image,
          order: index
        });
      });
      
      await Promise.all(updatePromises);
      refreshImages();
    } catch (error) {
      toast.error('Failed to reorder images');
      console.error("Reorder error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Set image for editing
  const editImage = (image) => {
    setEditMode(true);
    setCurrentImageId(image.id);
    setPreviewImages([image.url]);
    setAltTexts([image.altText]);
    setNewImages([]);
  };

  // Refresh images list
  const refreshImages = async () => {
    try {
      const sliderRef = ref(db, 'Watch/admin/slider/images');
      const snapshot = await get(sliderRef);
      
      if (snapshot.exists()) {
        const images = Object.entries(snapshot.val()).map(([id, image]) => ({
          id,
          ...image,
          order: image.order || 0
        }));
        // Sort images by order
        images.sort((a, b) => a.order - b.order);
        setSliderImages(images);
      } else {
        setSliderImages([]);
      }
    } catch (error) {
      console.error("Refresh error:", error);
    }
  };

  // Reset form
  const resetForm = () => {
    setNewImages([]);
    setPreviewImages([]);
    setAltTexts([]);
    setEditMode(false);
    setCurrentImageId(null);
    // Clear file input
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) fileInput.value = '';
  };

  // Handle drag start
  const handleDragStart = (index) => {
    setDragIndex(index);
  };

  // Handle drag over
  const handleDragOver = (e, index) => {
    e.preventDefault();
    setHoverIndex(index);
  };

  // Handle drop
  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    if (dragIndex !== null && dragIndex !== targetIndex) {
      reorderImages(dragIndex, targetIndex);
    }
    setHoverIndex(null);
  };

  // Navigation functions
  const nextSlide = () => {
    setActiveIndex(prev => (prev + 1) % sliderImages.length);
  };

  const prevSlide = () => {
    setActiveIndex(prev => (prev - 1 + sliderImages.length) % sliderImages.length);
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.header}>Manage Slider Images</h2>
      
      {/* Upload/Edit Section */}
      <div style={styles.uploadSection}>
        <h3 style={styles.sectionTitle}>
          {editMode ? 'Edit Slider Image' : 'Add New Slider Images'}
          {!editMode && <span style={styles.maxFilesHint}>(Max 10 files at once)</span>}
        </h3>
        
        <div style={styles.uploadArea}>
          <label style={styles.fileInputLabel}>
            <input 
              type="file" 
              accept="image/*"
              onChange={handleImageUpload}
              disabled={loading}
              style={styles.fileInput}
              multiple
              max={10}
            />
            <div style={styles.uploadBox}>
              <svg style={styles.uploadIcon} viewBox="0 0 24 24">
                <path d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z" />
              </svg>
              <p>Click to browse or drag and drop images</p>
              <p style={styles.supportedFormats}>Supports: JPG, PNG, GIF, WEBP</p>
            </div>
          </label>
        </div>
        
        {/* Preview and alt text inputs */}
        {previewImages.length > 0 && (
          <div style={styles.previewsContainer}>
            {previewImages.map((preview, index) => (
              <div key={index} style={styles.imagePreviewCard}>
                <img 
                  src={preview} 
                  alt={`Preview ${index + 1}`} 
                  style={styles.previewImage} 
                />
                <input
                  type="text"
                  value={altTexts[index] || ''}
                  onChange={(e) => {
                    const newAltTexts = [...altTexts];
                    newAltTexts[index] = e.target.value;
                    setAltTexts(newAltTexts);
                  }}
                  placeholder={`Description for image ${index + 1}`}
                  style={styles.altTextInput}
                />
              </div>
            ))}
            
            <div style={styles.actionButtons}>
              {editMode ? (
                <>
                  <button 
                    onClick={updateImage}
                    disabled={loading}
                    style={styles.updateButton}
                  >
                    {loading ? 'Updating...' : 'Update Image'}
                  </button>
                  <button 
                    onClick={resetForm}
                    disabled={loading}
                    style={styles.cancelButton}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button 
                    onClick={uploadImages}
                    disabled={loading}
                    style={styles.uploadButton}
                  >
                    {loading ? 'Uploading...' : `Upload ${previewImages.length} Image(s)`}
                  </button>
                  <button 
                    onClick={resetForm}
                    disabled={loading}
                    style={styles.clearButton}
                  >
                    Clear Selection
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Current Slider Images */}
      <div style={styles.currentImagesSection}>
        <h3 style={styles.sectionTitle}>
          Current Slider Images ({sliderImages.length})
          <span style={styles.dragDropHint}>(Drag to reorder)</span>
        </h3>
        
        {loading && !sliderImages.length ? (
          <div style={styles.loadingPlaceholder}>
            <div style={styles.spinner}></div>
            <p>Loading images...</p>
          </div>
        ) : sliderImages.length === 0 ? (
          <div style={styles.emptyState}>
            <svg style={styles.emptyIcon} viewBox="0 0 24 24">
              <path d="M5,3H19A2,2 0 0,1 21,5V19A2,2 0 0,1 19,21H5A2,2 0 0,1 3,19V5A2,2 0 0,1 5,3M5,5V19H19V5H5M7,7H17V9H7V7M7,11H17V13H7V11M7,15H14V17H7V15Z" />
            </svg>
            <p>No images found. Upload some images to get started.</p>
          </div>
        ) : (
          <>
            {/* Slider Preview */}
            <div style={styles.sliderContainer}>
              <button style={styles.leftArrowButton} onClick={prevSlide}>&#10094;</button>
              
              <div style={styles.slideWrapper}>
                {sliderImages.map((image, index) => (
                  <div 
                    key={image.id}
                    style={{
                      ...styles.slide,
                      opacity: index === activeIndex ? 1 : 0
                    }}
                  >
                    <img src={image.url} alt={image.altText} style={styles.slideImage} />
                    <div style={styles.imageInfo}>
                      <p style={styles.imageAltText}>{image.altText}</p>
                      <div style={styles.imageActions}>
                        <button 
                          style={styles.editButton}
                          onClick={() => editImage(image)}
                          disabled={loading}
                        >
                          Edit
                        </button>
                        <button 
                          style={styles.deleteButton}
                          onClick={() => deleteImage(image.id, image)}
                          disabled={loading}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <button style={styles.rightArrowButton} onClick={nextSlide}>&#10095;</button>
            </div>
            
            {/* Image Thumbnail List */}
            <div style={styles.thumbnailGrid}>
              {sliderImages.map((image, index) => (
                <div 
                  key={image.id}
                  style={{
                    ...styles.thumbnailCard,
                    borderColor: index === activeIndex ? '#2a7de1' : '#eee',
                    backgroundColor: hoverIndex === index ? '#f0f7ff' : 'white',
                    opacity: loading ? 0.7 : 1
                  }}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={() => setHoverIndex(null)}
                >
                  <div style={styles.thumbnailNumber}>{index + 1}</div>
                  <img 
                    src={image.url} 
                    alt={`Thumbnail ${index + 1}`} 
                    style={styles.thumbnailImage} 
                    onClick={() => setActiveIndex(index)}
                  />
                  <div style={styles.thumbnailActions}>
                    <button 
                      style={styles.smallEditButton}
                      onClick={() => editImage(image)}
                      disabled={loading}
                    >
                      Edit
                    </button>
                    <button 
                      style={styles.smallDeleteButton}
                      onClick={() => deleteImage(image.id, image)}
                      disabled={loading}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Enhanced Styles
const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '30px 20px',
    fontFamily: '"Segoe UI", Roboto, "Helvetica Neue", sans-serif',
    backgroundColor: '#f9fafb',
    borderRadius: '12px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
  },
  header: {
    marginBottom: '30px',
    color: '#2d3748',
    textAlign: 'center',
    fontSize: '28px',
    fontWeight: '600',
    borderBottom: '2px solid #e2e8f0',
    paddingBottom: '15px'
  },
  sectionTitle: {
    color: '#2d3748',
    fontSize: '20px',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  maxFilesHint: {
    fontSize: '14px',
    color: '#718096',
    fontWeight: 'normal'
  },
  dragDropHint: {
    fontSize: '14px',
    color: '#718096',
    fontWeight: 'normal',
    marginLeft: '10px'
  },
  uploadSection: {
    marginBottom: '40px',
    padding: '25px',
    backgroundColor: 'white',
    borderRadius: '10px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  currentImagesSection: {
    padding: '25px',
    backgroundColor: 'white',
    borderRadius: '10px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  uploadArea: {
    marginBottom: '20px'
  },
  fileInputLabel: {
    display: 'block',
    cursor: 'pointer'
  },
  fileInput: {
    display: 'none'
  },
  uploadBox: {
    border: '2px dashed #cbd5e0',
    borderRadius: '8px',
    padding: '40px 20px',
    textAlign: 'center',
    transition: 'all 0.3s ease',
    backgroundColor: '#f8fafc',
    ':hover': {
      borderColor: '#2a7de1',
      backgroundColor: '#f0f7ff'
    }
  },
  uploadIcon: {
    width: '48px',
    height: '48px',
    fill: '#a0aec0',
    marginBottom: '15px'
  },
  supportedFormats: {
    fontSize: '12px',
    color: '#718096',
    marginTop: '5px'
  },
  previewsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  imagePreviewCard: {
    display: 'flex',
    gap: '20px',
    alignItems: 'center',
    padding: '15px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    border: '1px solid #e2e8f0'
  },
  previewImage: {
    width: '100px',
    height: '100px',
    objectFit: 'cover',
    borderRadius: '6px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
  },
  altTextInput: {
    flex: 1,
    padding: '10px 15px',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    fontSize: '14px',
    ':focus': {
      outline: 'none',
      borderColor: '#2a7de1',
      boxShadow: '0 0 0 3px rgba(42, 125, 225, 0.2)'
    }
  },
  actionButtons: {
    display: 'flex',
    gap: '15px',
    marginTop: '20px'
  },
  uploadButton: {
    padding: '12px 24px',
    backgroundColor: '#2a7de1',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
    transition: 'background-color 0.2s',
    ':hover': {
      backgroundColor: '#1e6dc8'
    },
    ':disabled': {
      backgroundColor: '#a0aec0',
      cursor: 'not-allowed'
    }
  },
  updateButton: {
    padding: '12px 24px',
    backgroundColor: '#27ae60',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
    transition: 'background-color 0.2s',
    ':hover': {
      backgroundColor: '#219653'
    },
    ':disabled': {
      backgroundColor: '#a0aec0',
      cursor: 'not-allowed'
    }
  },
  cancelButton: {
    padding: '12px 24px',
    backgroundColor: '#e67e22',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
    transition: 'background-color 0.2s',
    ':hover': {
      backgroundColor: '#d35400'
    },
    ':disabled': {
      backgroundColor: '#a0aec0',
      cursor: 'not-allowed'
    }
  },
  clearButton: {
    padding: '12px 24px',
    backgroundColor: 'transparent',
    color: '#e53e3e',
    border: '1px solid #e53e3e',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
    transition: 'all 0.2s',
    ':hover': {
      backgroundColor: '#fff5f5'
    },
    ':disabled': {
      color: '#a0aec0',
      borderColor: '#a0aec0',
      cursor: 'not-allowed'
    }
  },
  sliderContainer: {
    position: 'relative',
    margin: '30px 0',
    display: 'flex',
    alignItems: 'center',
    minHeight: '450px',
    backgroundColor: '#f1f5f9',
    borderRadius: '10px',
    overflow: 'hidden',
    paddingBottom: '50px'
  },
  leftArrowButton: {
    position: 'absolute',
    top: '50%',
    left: '20px',
    transform: 'translateY(-50%)',
    backgroundColor: 'rgba(0,0,0,0.5)',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    padding: '20px',
    fontSize: '24px',
    zIndex: 10,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '50px',
    height: '50px',
    transition: 'all 0.2s',
    ':hover': {
      backgroundColor: 'rgba(0,0,0,0.7)'
    }
  },
  rightArrowButton: {
    position: 'absolute',
    top: '50%',
    right: '20px',
    transform: 'translateY(-50%)',
    backgroundColor: 'rgba(0,0,0,0.5)',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    padding: '20px',
    fontSize: '24px',
    zIndex: 10,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '50px',
    height: '50px',
    transition: 'all 0.2s',
    ':hover': {
      backgroundColor: 'rgba(0,0,0,0.7)'
    }
  },
  slideWrapper: {
    width: '100%',
    height: '450px',
    position: 'relative'
  },
  slide: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'opacity 0.5s ease',
    padding: '20px'
  },
  slideImage: {
    maxWidth: '100%',
    maxHeight: '350px',
    objectFit: 'contain',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
  },
  imageInfo: {
    marginTop: '20px',
    textAlign: 'center',
    maxWidth: '80%'
  },
  imageAltText: {
    fontSize: '16px',
    color: '#2d3748',
    marginBottom: '15px',
    fontWeight: '500'
  },
  imageActions: {
    display: 'flex',
    justifyContent: 'center',
    gap: '15px'
  },
  editButton: {
    padding: '8px 20px',
    backgroundColor: '#3182ce',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'background-color 0.2s',
    ':hover': {
      backgroundColor: '#2c5282'
    },
    ':disabled': {
      backgroundColor: '#a0aec0',
      cursor: 'not-allowed'
    }
  },
  deleteButton: {
    padding: '8px 20px',
    backgroundColor: '#e53e3e',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'background-color 0.2s',
    ':hover': {
      backgroundColor: '#c53030'
    },
    ':disabled': {
      backgroundColor: '#a0aec0',
      cursor: 'not-allowed'
    }
  },
  thumbnailGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '20px',
    marginTop: '30px'
  },
  thumbnailCard: {
    border: '2px solid #eee',
    borderRadius: '8px',
    padding: '15px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    transition: 'all 0.3s ease',
    position: 'relative',
    ':hover': {
      transform: 'translateY(-5px)',
      boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
    }
  },
  thumbnailNumber: {
    position: 'absolute',
    top: '5px',
    left: '5px',
    backgroundColor: '#2a7de1',
    color: 'white',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 'bold'
  },
  thumbnailImage: {
    width: '100%',
    height: '120px',
    objectFit: 'cover',
    borderRadius: '6px',
    cursor: 'pointer'
  },
  thumbnailActions: {
    display: 'flex',
    gap: '10px',
    marginTop: '15px',
    width: '100%'
  },
  smallEditButton: {
    flex: 1,
    padding: '6px 12px',
    backgroundColor: '#3182ce',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    transition: 'background-color 0.2s',
    ':hover': {
      backgroundColor: '#2c5282'
    },
    ':disabled': {
      backgroundColor: '#a0aec0',
      cursor: 'not-allowed'
    }
  },
  smallDeleteButton: {
    flex: 1,
    padding: '6px 12px',
    backgroundColor: '#e53e3e',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    transition: 'background-color 0.2s',
    ':hover': {
      backgroundColor: '#c53030'
    },
    ':disabled': {
      backgroundColor: '#a0aec0',
      cursor: 'not-allowed'
    }
  },
  loadingPlaceholder: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    color: '#718096'
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid rgba(0,0,0,0.1)',
    borderLeftColor: '#2a7de1',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '20px'
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    color: '#718096',
    border: '2px dashed #e2e8f0',
    borderRadius: '8px',
    backgroundColor: '#f8fafc'
  },
  emptyIcon: {
    width: '60px',
    height: '60px',
    fill: '#cbd5e0',
    marginBottom: '20px'
  }
};

export default AdminSlider;